import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

interface CarouselItem {
  image: string;
  title: string;
  description: string;
  link?: string;
}

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss']
})
export class CarouselComponent implements OnInit, OnDestroy {

  @ViewChild('carouselContainer') carouselContainer!: ElementRef;

  items: CarouselItem[] = [];
  displayedItems: CarouselItem[] = [];

  currentIndex = 1;
  transform = 'translateX(-100%)';

  autoSlideInterval: any;
  isSliding = false;

  touchStartX = 0;
  touchEndX = 0;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(() => this.loadCarouselItems());
    this.loadCarouselItems();
  }

  ngAfterViewInit(): void {
    this.initAutoSlide();
    this.initTouchEvents();
  }

  ngOnDestroy(): void {
    clearInterval(this.autoSlideInterval);
  }

  isDaytime(): boolean {
    const now = new Date();
    const hour = now.getHours();
    return hour >= 6 && hour < 16;
  }

  loadCarouselItems(): void {
    const dayTime = this.isDaytime();
    const storeImage = dayTime
      ? 'assets/images/store-front-peony.jpg'
      : 'assets/images/store-front-peony.jpg'
      //: 'assets/images/carousel-store-front-night.jpg';

    this.translate.get([
      'carousel.item3',
      'carousel.item4',
      'carousel.item5',
      'carousel.item6'
    ]).subscribe((res: any) => {
      const carouselConfig = [
        {
          key: 'carousel.item3',
          image: 'assets/images/carousel-order-wolt.jpg',
          link: 'https://shop.twogetherflower.hu'
        },
        {
          key: 'carousel.item4',
          image: storeImage
        },
        {
          key: 'carousel.item5',
          image: 'assets/images/carousel-cutted-flowers.jpg'
        },
        {
          key: 'carousel.item6',
          image: 'assets/images/carousel-cutted-flowers.jpg'
        }
      ];

      const items = carouselConfig
        .map((item): CarouselItem | null => {
          const translation = res[item.key];

          if (!translation?.title || !translation?.description) {
            return null;
          }

          return {
            image: item.image,
            title: translation.title,
            description: translation.description,
            link: item.link
          };
        })
        .filter((item): item is CarouselItem => item !== null);

      this.items = items;

      // Klónok
      this.displayedItems = [
        this.items[this.items.length - 1],
        ...this.items,
        this.items[0]
      ];
    });
  }

  initAutoSlide() {
    this.autoSlideInterval = setInterval(() => this.next(), 8000);
  }

  resetAutoSlide() {
    clearInterval(this.autoSlideInterval);
    this.autoSlideInterval = setInterval(() => this.next(), 8000);
  }

  /** NEXT */
  next() {
    if (this.isSliding) return;
    this.isSliding = true;

    this.currentIndex++;
    this.updateTransform();

    setTimeout(() => {
      if (this.currentIndex === this.items.length + 1) {
        this.currentIndex = 1;
        this.disableTransition();
        this.updateTransform();
        setTimeout(() => this.enableTransition(), 20);
      }
      this.isSliding = false;
    }, 600);

    this.resetAutoSlide();
  }

  /** PREV */
  prev() {
    if (this.isSliding) return;
    this.isSliding = true;

    this.currentIndex--;
    this.updateTransform();

    setTimeout(() => {
      if (this.currentIndex === 0) {
        this.currentIndex = this.items.length;
        this.disableTransition();
        this.updateTransform();
        setTimeout(() => this.enableTransition(), 20);
      }
      this.isSliding = false;
    }, 600);

    this.resetAutoSlide();
  }

  /** Touch handler (swipe) */
  initTouchEvents() {
    const el = this.carouselContainer.nativeElement;

    el.addEventListener('touchstart', (e: TouchEvent) => {
      this.touchStartX = e.touches[0].clientX;
    });

    el.addEventListener('touchend', (e: TouchEvent) => {
      this.touchEndX = e.changedTouches[0].clientX;
      const diff = this.touchEndX - this.touchStartX;
      if (Math.abs(diff) > 50) diff < 0 ? this.next() : this.prev();
    });
  }

  updateTransform() {
    this.transform = `translateX(-${this.currentIndex * 100}%)`;
  }

  disableTransition() {
    (this.carouselContainer.nativeElement.querySelector('.carousel-track') as HTMLElement).style.transition = 'none';
  }

  enableTransition() {
    (this.carouselContainer.nativeElement.querySelector('.carousel-track') as HTMLElement).style.transition = 'transform 0.6s ease';
  }
}
